import type { FieldDefinition, Locale } from '@sentra/contracts';
import { isMessageKey, translate } from '@sentra/contracts';

import { isApiError } from '@/api/errors';
import { labelOf } from '@/features/persons/form/values';

/**
 * The refusals the SERVER attached to individual fields, as problems this form can render.
 *
 * **Why the browser cannot simply render the server's sentence.** An error body carries a code, a
 * rendered message and a `details` list; it does not carry the message's parameters. So a message
 * with a placeholder only the server filled — `PERSON_DUPLICATE_UNIQUE_VALUE` names a field —
 * reaches `failureMessage` unfillable, which falls back to the server's own rendering in the
 * language the REQUEST negotiated rather than the one this operator chose. And it lands in the
 * banner above the form rather than on the control the refusal is about, which on a thirty-five
 * field form is the difference between a correction and a search.
 *
 * `details` is what closes both. Every producer in this system names the request section it refused
 * — `body.fields.<key>` for a person's field — so a path is a field, a `messageKey` is a catalogue
 * key, and this side has the registry: it can render the sentence in the operator's own language
 * with the field's LABEL where the server had only a machine key.
 *
 * It is deliberately narrow. A detail whose path is not `body.fields.<key>`, or whose key names no
 * field the form is rendering, is ignored here and left to the banner, because a problem attached to
 * a control that is not on screen is a problem nobody can act on.
 */
export function serverFieldProblems(
  error: unknown,
  definitions: readonly FieldDefinition[],
  locale: Locale,
): Record<string, string> {
  if (!isApiError(error)) {
    return {};
  }
  const byKey = new Map(definitions.map((definition) => [definition.key, definition]));
  const problems: Record<string, string> = {};

  for (const detail of error.details) {
    const key = detail.path.startsWith(FIELD_PATH_PREFIX)
      ? detail.path.slice(FIELD_PATH_PREFIX.length)
      : null;
    const definition = key === null ? undefined : byKey.get(key);
    if (key === null || definition === undefined || !isMessageKey(detail.messageKey)) {
      continue;
    }
    // The field's own label as `fieldKey`, which is the one placeholder these messages take. The
    // server could not have supplied it: it holds two labels and no idea which language to pick.
    const params = { ...detail.params, fieldKey: labelOf(definition, locale) };
    problems[key] = translate(locale, detail.messageKey, params);
  }
  return problems;
}

/** The one namespace a person's field refusal is reported under (`http/validate.ts`'s contract). */
const FIELD_PATH_PREFIX = 'body.fields.';

/**
 * The name this form would give one detail path, or `null` when it is not about a field it renders.
 *
 * The other half of {@link serverFieldProblems}: that one attaches what it can to a control, and
 * this lets the banner say what the rest was about in words rather than in `fields › passport_number`.
 * Both read the same registry, so a path either becomes a label or falls through to the humanised
 * form — never to a machine key dressed up as prose.
 */
export function labelForFieldPath(
  path: string,
  definitions: readonly FieldDefinition[],
  locale: Locale,
): string | null {
  if (!path.startsWith(FIELD_PATH_PREFIX)) {
    return null;
  }
  const key = path.slice(FIELD_PATH_PREFIX.length);
  const definition = definitions.find((one) => one.key === key);
  return definition === undefined ? null : labelOf(definition, locale);
}
